import React, { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { plyxApi } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, Crown, Check, Sparkles, Video, Award, Upload, TrendingUp, Eye } from "lucide-react";
import { toast } from "sonner";

/**
 * /plyx/pro — Plyx Pro subscription landing & checkout.
 *
 * Renders a single decisive call-to-action page:
 *   • Hero (gradient + Crown icon)
 *   • Current status (free / Pro until <date>)
 *   • Five benefit cards
 *   • Subscribe button → Stripe Checkout
 *
 * Design intent: short, vertical, mobile-first. Avoids the "AI-slop"
 * dashboard pattern of N feature cards in a grid; uses asymmetric
 * spacing + a single bold accent (the Pro gradient).
 */
const BENEFIT_ICONS = {
  "Auto-record every live stream": Video,
  "Pro badge on profile + feed": Award,
  "Higher upload size cap (250MB)": Upload,
  "Priority \u201cFor You\u201d boost": TrendingUp,
  "Ad-free feed": Eye,
};

export default function PlyxPro() {
  const { user, loading } = usePlyxAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    if (loading) return;
    if (!user) return;
    plyxApi
      .get("/plyx/pro/status")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load Pro status"));
  }, [loading, user]);

  useEffect(() => {
    // Returning from Stripe success — invite the user to refresh after
    // ~3s so the webhook has time to flip is_pro. We don't poll: the
    // webhook is fast and a manual re-mount on next page visit is fine.
    if (params.get("session_id")) {
      toast.success("Payment received — your Pro perks are unlocking now…");
    } else if (params.get("cancelled")) {
      toast.info("Checkout cancelled.");
    }
  }, [params]);

  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-black text-white">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  if (!user) return <Navigate to="/plyx/login?next=/plyx/pro" replace />;

  const startCheckout = async () => {
    setBusy(true);
    try {
      // True recurring monthly subscription — Stripe auto-renews via
      // `invoice.paid` webhooks. Falls back to the one-time grant
      // endpoint if the recurring price isn't provisioned yet (e.g.
      // Stripe Dashboard is mid-claim).
      let r;
      try {
        r = await plyxApi.post("/plyx/pro/checkout-subscription", {
          origin_url: window.location.origin,
        });
      } catch (subErr) {
        if (subErr?.response?.status === 503) {
          r = await plyxApi.post("/plyx/pro/checkout", {
            origin_url: window.location.origin,
          });
        } else {
          throw subErr;
        }
      }
      if (r.data?.url) {
        window.location.assign(r.data.url);
      } else {
        throw new Error("No checkout url");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const cancelSubscription = async () => {
    if (!window.confirm("Cancel auto-renewal? You'll keep Pro until your current month ends.")) return;
    setBusy(true);
    try {
      await plyxApi.post("/plyx/pro/cancel");
      toast.success("Subscription cancelled — Pro stays active until period end.");
      const r = await plyxApi.get("/plyx/pro/status");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally {
      setBusy(false);
    }
  };

  const priceLabel = data
    ? `\u00a3${Number(data.price).toFixed(2)}`
    : "\u00a33.99";

  return (
    <div
      className="min-h-[100dvh] bg-black text-white pb-32"
      data-testid="plyx-pro-page"
    >
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-30 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(60% 60% at 50% 0%, rgba(255,45,122,0.45), transparent 70%), radial-gradient(50% 50% at 90% 30%, rgba(0,224,199,0.35), transparent 70%)",
          }}
        />
        <div className="relative px-6 pt-16 pb-10 max-w-xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/5 border border-white/10 text-[11px] font-semibold tracking-wide text-white/80">
            <Sparkles className="w-3.5 h-3.5 text-[#FFB42D]" /> PLYX PRO
          </div>
          <h1
            className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05]"
            style={{
              backgroundImage:
                "#E85D4E",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            Go Pro.
            <br />
            Get every live moment.
          </h1>
          <p className="text-base text-white/70 mt-4 max-w-md">
            Auto-record your streams, wear the Pro badge, and skip ahead in
            the For You queue. Cancel anytime.
          </p>

          {/* Status pill */}
          {data?.is_pro ? (
            <div
              className="mt-7 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#FF2D7A]/20 via-[#FFB42D]/20 to-[#00E0C7]/20 border border-white/15 text-sm"
              data-testid="plyx-pro-active-pill"
            >
              <Crown className="w-4 h-4 text-[#FFB42D]" />
              <span className="font-semibold">
                Pro active until{" "}
                {new Date(data.pro_until).toLocaleDateString(undefined, {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}
              </span>
            </div>
          ) : (
            <div
              className="mt-7 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-white/70"
              data-testid="plyx-pro-free-pill"
            >
              Free plan
            </div>
          )}
        </div>
      </div>

      {/* Benefits list — left-aligned, generous spacing */}
      <div className="px-6 max-w-xl mx-auto mt-2 space-y-4">
        {(data?.benefits || Object.keys(BENEFIT_ICONS)).map((b) => {
          const Icon = BENEFIT_ICONS[b] || Check;
          return (
            <div
              key={b}
              className="flex items-start gap-4 py-4 border-b border-white/5"
              data-testid={`plyx-pro-benefit-${b.split(" ")[0].toLowerCase()}`}
            >
              <div className="w-10 h-10 shrink-0 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                <Icon className="w-5 h-5 text-white/85" />
              </div>
              <div className="flex-1">
                <div className="text-base font-semibold">{b}</div>
              </div>
              <Check className="w-5 h-5 text-[#FFB42D] mt-2.5 shrink-0" />
            </div>
          );
        })}
      </div>

      {/* Pricing footer + CTA */}
      <div className="px-6 max-w-xl mx-auto mt-10">
        <div className="rounded-2xl p-6 bg-white/5 border border-white/10">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="text-sm text-white/60">Plyx Pro</div>
              <div className="font-display text-4xl mt-1">
                {priceLabel}
                <span className="text-base text-white/50 ml-1">
                  / {data?.duration_days || 30} days
                </span>
              </div>
            </div>
            <div className="text-xs text-white/50">No commitment</div>
          </div>

          <button
            type="button"
            onClick={startCheckout}
            disabled={busy || (data?.is_pro && data?.has_subscription && !data?.cancel_at_period_end)}
            data-testid="plyx-pro-subscribe-btn"
            className="mt-6 w-full py-4 rounded-xl font-semibold text-base text-black bg-gradient-to-r from-[#FF2D7A] via-[#FFB42D] to-[#00E0C7] hover:brightness-110 transition disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {busy ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : data?.is_pro && data?.has_subscription && !data?.cancel_at_period_end ? (
              <>
                <Crown className="w-5 h-5" /> Auto-renewing monthly
              </>
            ) : data?.is_pro && data?.has_subscription && data?.cancel_at_period_end ? (
              <>Resubscribe for {priceLabel}/mo</>
            ) : data?.is_pro ? (
              // One-time-grant user (no recurring subscription yet) — let
              // them upgrade to auto-renew so future months are billed
              // automatically and they get the cancel button too.
              <>Switch to auto-renew · {priceLabel}/mo</>
            ) : (
              <>Subscribe for {priceLabel}/mo</>
            )}
          </button>

          {/* Cancel-at-period-end button — only shown when an actual
              recurring subscription is on file. The one-time-grant flow
              has nothing to cancel. */}
          {data?.has_subscription && !data?.cancel_at_period_end && (
            <button
              type="button"
              onClick={cancelSubscription}
              disabled={busy}
              data-testid="plyx-pro-cancel-btn"
              className="mt-3 w-full py-2.5 rounded-xl text-sm text-white/70 border border-white/10 hover:bg-white/5 transition disabled:opacity-50"
            >
              Cancel auto-renewal
            </button>
          )}
          {data?.cancel_at_period_end && (
            <p className="text-xs text-[#FFB42D] mt-3 text-center" data-testid="plyx-pro-cancel-notice">
              Auto-renew cancelled. Pro stays active until{" "}
              {data?.pro_until
                ? new Date(data.pro_until).toLocaleDateString(undefined, { day: "numeric", month: "short" })
                : "period end"}.
            </p>
          )}

          <p className="text-[11px] text-white/45 mt-4 leading-relaxed text-center">
            Powered by Stripe. £3.99 billed monthly in GBP. Cancel anytime —
            your Pro window stays active through the end of the billing cycle.
          </p>
        </div>

        <div className="text-center mt-6">
          <Link to="/plyx" className="text-sm text-white/50 underline">
            Back to feed
          </Link>
        </div>
      </div>
    </div>
  );
}
